import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'

function commandBinding(command) {
  return command?.effectiveBinding ?? command?.binding ?? null
}

function bindingParts(binding) {
  if (Array.isArray(binding)) return binding.filter(Boolean).map(String)
  if (binding === null || binding === undefined || binding === '') return []
  return [String(binding)]
}

function isAvailable(command) {
  return command?.available !== false && !command?.unavailableReason
}

function trapDialogFocus(event, container, first, last) {
  if (event.key !== 'Tab' || !container || !first || !last) return
  if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function searchText(command) {
  const keywords = Array.isArray(command?.keywords)
    ? command.keywords.join(' ')
    : command?.keywords || ''
  return [
    command?.label,
    command?.description,
    command?.scope,
    keywords,
    ...bindingParts(commandBinding(command)),
  ].filter(Boolean).join(' ').toLowerCase()
}

function ShortcutKeys({ binding }) {
  const parts = bindingParts(binding)
  if (parts.length === 0) return null

  return (
    <span className="ml-3 flex shrink-0 items-center gap-1" aria-label={`Shortcut ${parts.join(' then ')}`}>
      {parts.map((part, index) => (
        <kbd
          key={`${part}-${index}`}
          className="min-w-6 rounded border border-border bg-canvas px-1.5 py-0.5 text-center font-mono text-[10px] font-semibold text-inkMuted shadow-sm"
        >
          {part}
        </kbd>
      ))}
    </span>
  )
}

export default function CommandPalette({
  open = true,
  commands = [],
  onSelect,
  onClose,
  title = 'Command palette',
  initialQuery = '',
}) {
  const reactId = useId()
  const listboxId = `command-palette-${reactId.replace(/:/g, '')}`
  const dialogRef = useRef(null)
  const inputRef = useRef(null)
  const closeRef = useRef(null)
  const [query, setQuery] = useState(initialQuery)
  const [activeIndex, setActiveIndex] = useState(0)

  const filteredCommands = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return commands
    return commands.filter(command => searchText(command).includes(normalizedQuery))
  }, [commands, query])

  useEffect(() => {
    if (!open) return
    setQuery(initialQuery)
    setActiveIndex(0)
  }, [initialQuery, open])

  useEffect(() => {
    if (!open) return undefined
    const priorFocus = document.activeElement
    inputRef.current?.focus()
    return () => {
      if (priorFocus instanceof HTMLElement && priorFocus.isConnected) priorFocus.focus()
    }
  }, [open])

  useEffect(() => {
    if (activeIndex < filteredCommands.length) return
    setActiveIndex(Math.max(0, filteredCommands.length - 1))
  }, [activeIndex, filteredCommands.length])

  if (!open) return null

  const activeCommand = filteredCommands[activeIndex] || null
  const activeOptionId = activeCommand ? `${listboxId}-option-${activeIndex}` : undefined

  const selectCommand = (command) => {
    if (!isAvailable(command)) return
    onSelect?.(command)
  }

  const handleKeyDown = (event) => {
    if (event.nativeEvent?.isComposing) return

    trapDialogFocus(event, dialogRef.current, inputRef.current, closeRef.current)
    if (event.key === 'Tab') return

    if (event.key === 'Escape') {
      event.preventDefault()
      onClose?.()
      return
    }

    if (filteredCommands.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex(index => (index + 1) % filteredCommands.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(index => (index - 1 + filteredCommands.length) % filteredCommands.length)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      if (activeCommand) selectCommand(activeCommand)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-ink/20 px-4 pt-[12vh] backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.()
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${listboxId}-title`}
        className="w-full max-w-2xl overflow-hidden rounded-xl border border-border bg-canvas shadow-2xl shadow-ink/20"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search size={17} className="shrink-0 text-inkMuted" aria-hidden="true" />
          <h2 id={`${listboxId}-title`} className="sr-only">{title}</h2>
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-label="Search commands"
            aria-controls={listboxId}
            aria-expanded="true"
            aria-autocomplete="list"
            aria-activedescendant={activeOptionId}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setActiveIndex(0)
            }}
            placeholder="Search commands…"
            className="h-14 min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-inkDim"
          />
          <button
            ref={closeRef}
            type="button"
            onClick={() => onClose?.()}
            className="rounded-md p-1.5 text-inkMuted transition-colors hover:bg-canvas2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            aria-label="Close command palette"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div id={listboxId} role="listbox" aria-label="Commands" className="max-h-[min(62vh,32rem)] overflow-y-auto p-2">
          {filteredCommands.length === 0 ? (
            <div role="status" className="px-4 py-10 text-center text-sm text-inkMuted">
              No commands found
            </div>
          ) : filteredCommands.map((command, index) => {
            const available = isAvailable(command)
            const active = index === activeIndex
            return (
              <button
                key={command.id || `${command.label}-${index}`}
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={active}
                aria-disabled={!available}
                disabled={!available}
                onMouseMove={() => setActiveIndex(index)}
                onClick={() => selectCommand(command)}
                className={`flex w-full items-start rounded-lg px-3 py-2.5 text-left outline-none transition-colors ${
                  active ? 'bg-canvas2' : 'bg-transparent'
                } ${
                  available
                    ? 'text-ink hover:bg-canvas2'
                    : 'cursor-not-allowed text-inkDim'
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-semibold">{command.label}</span>
                    {command.scope && (
                      <span className="shrink-0 font-mono text-[9px] uppercase tracking-widest text-inkDim">
                        {command.scope}
                      </span>
                    )}
                  </span>
                  {command.description && (
                    <span className="mt-0.5 block text-xs text-inkMuted">{command.description}</span>
                  )}
                  {!available && command.unavailableReason && (
                    <span className="mt-1 block text-xs font-medium text-ember">{command.unavailableReason}</span>
                  )}
                </span>
                <ShortcutKeys binding={commandBinding(command)} />
              </button>
            )
          })}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-canvas2/60 px-4 py-2 font-mono text-[10px] text-inkMuted">
          <span>↑↓ Navigate</span>
          <span>Enter Run</span>
          <span>Esc Close</span>
        </div>
      </section>
    </div>
  )
}
